import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faChevronLeft,
    faChevronRight,
    faFileImport,
    faKeyboard,
    faPen,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../FlashMessage';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { extractApiError } from '../../utils/errorMessages';
import { deleteManualPrice, fetchDailyPrices } from '../../services/paperPricingService';
import {
    BATE_LABELS,
    PRICE_POINTS,
    formatCount,
    formatDateTime,
    formatDay,
    formatPrice,
    plural,
    requireList,
    unitLabel,
} from './paperPricingFormat';
import { Dash, LoadProblem, StatusDot, textOrDash } from './PaperPricingParts';
import RowMenu from './RowMenu';
import ImportPriceFileModal from './ImportPriceFileModal';
import EnterPriceModal from './EnterPriceModal';
import PriceHistoryModal from './PriceHistoryModal';

const COLUMNS = 10;

const priceLabelFor = (bate) => BATE_LABELS[bate] || String(bate || '').toUpperCase();

/** One filter box. A chosen value the day's list no longer carries stays chosen and visible. */
const FilterSelect = ({ id, label, value, onChange, options, allLabel, disabled }) => {
    const shown = !value || options.some((option) => option.value === value)
        ? options
        : [...options, { value, label: value }];
    return (
        <div className="pp-filter">
            <label className="pp-filter-label" htmlFor={id}>{label}</label>
            <select
                id={id}
                className="pp-select"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                disabled={disabled}
            >
                {allLabel ? <option value="">{allLabel}</option> : null}
                {shown.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
        </div>
    );
};

const PriceStatus = ({ assessment }) => {
    if (!assessment) return <StatusDot tone="grey">No price</StatusDot>;
    if (assessment.status === 'confirmed') {
        return <StatusDot tone="green">{assessment.status_label}</StatusDot>;
    }
    if (assessment.status === 'manual') {
        const who = assessment.entered_by_name
            ? `Typed by ${assessment.entered_by_name}, ${formatDateTime(assessment.updated_at)}`
            : undefined;
        return <StatusDot tone="amber" title={who}>{assessment.status_label}</StatusDot>;
    }
    return <StatusDot tone="grey">{assessment.status_label}</StatusDot>;
};

const importSummary = (record) => {
    const counts = record?.counts;
    if (!counts) return `Imported ${record?.file_name || 'the price file'}.`;
    return `Imported ${record.file_name}: ${formatCount(counts.new)} new, ${formatCount(counts.changed)} changed, `
        + `${formatCount(counts.replaced_manual)} manual prices replaced, `
        + `${formatCount(counts.new_benchmarks)} new benchmark codes.`;
};

/**
 * Market Prices (ledger row 132, AC6-AC13): one pricing day of every active
 * benchmark, the day's summary and the last upload, the price-file import and
 * prices typed by hand.
 *
 * The board, its filters and the previous / next pricing days all come from the
 * server (prices/daily). This screen never guesses which day has prices.
 */
const MarketPricesTab = () => {
    const { showSuccess } = useFlashMessage();
    // An empty date asks the server for its latest day with a price.
    const [query, setQuery] = useState({ date: '', region: '', product: '', unit: '', bate: 'c' });
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [importing, setImporting] = useState(false);
    const [priceForm, setPriceForm] = useState(null);
    const [historyFor, setHistoryFor] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        fetchDailyPrices(query)
            .then((data) => {
                ['rows', 'regions', 'products', 'units'].forEach((key) => requireList(data, key));
                if (!cancelled) setBoard(data);
            })
            .catch((err) => {
                if (cancelled) return;
                // An old day left on screen under an error would read as today's prices.
                setBoard(null);
                setError(extractApiError(err, 'The price board could not be loaded. Try again.'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [query]);

    const reload = () => setQuery((current) => ({ ...current }));

    // A filter keeps the day on screen. Left empty, the date would ask the
    // server for its latest day again and the board could jump to another day.
    const setFilter = (field, value) => setQuery((current) => ({
        ...current,
        date: current.date || board?.date || '',
        [field]: value,
    }));

    const goToDay = (day) => {
        if (day) setQuery((current) => ({ ...current, date: day }));
    };

    const priceLabel = priceLabelFor(board?.bate || query.bate);
    // Checked on arrival (requireList), so these are only empty while nothing is loaded.
    const rows = board ? board.rows : [];
    const regionOptions = board ? board.regions : [];
    const productOptions = board ? board.products.map((product) => ({ value: product, label: product })) : [];
    const unitOptions = board ? board.units.map((unit) => ({ value: unit, label: unit })) : [];
    const summary = board?.summary;
    const lastImport = board?.last_import;
    const filtered = Boolean(query.region || query.product || query.unit);

    const handlePriceSaved = ({ date, bate, message }) => {
        setPriceForm(null);
        showSuccess(message);
        // Show the day and price point the price was saved on, so it can be seen.
        setQuery((current) => ({ ...current, date, bate }));
    };

    const handleImported = (record) => {
        setImporting(false);
        showSuccess(importSummary(record), 7000);
        // Back to the latest day: that is the day a new file most likely moved.
        setQuery((current) => ({ ...current, date: '' }));
    };

    const confirmDelete = async () => {
        setDeleteBusy(true);
        try {
            await deleteManualPrice(deleting.row.assessment.id);
            showSuccess(`Deleted the manual ${priceLabelFor(deleting.bate)} price for ${deleting.row.benchmark.code} on ${formatDay(deleting.date)}.`);
            setDeleting(null);
            reload();
        } finally {
            // A refusal is shown by the confirmation window itself, in the server's words.
            setDeleteBusy(false);
        }
    };

    const emptyText = filtered
        ? 'No active benchmark matches these filters.'
        : 'There are no active benchmarks yet. Import a price file or add a benchmark on Benchmarks & Instruments.';

    return (
        <div className="pp-panel">
            <div className="pp-panel-head">
                <div>
                    <h2 className="pp-panel-title">Market Prices</h2>
                    <p className="pp-panel-subtitle">
                        Daily benchmark prices used for floating pricing and paper trades.
                    </p>
                </div>
                <div className="pp-actions">
                    <button
                        type="button"
                        className="pp-btn pp-btn--secondary"
                        onClick={() => setPriceForm({ mode: 'add' })}
                    >
                        <FontAwesomeIcon icon={faKeyboard} /> Enter price
                    </button>
                    <button
                        type="button"
                        className="pp-btn pp-btn--primary"
                        onClick={() => setImporting(true)}
                    >
                        <FontAwesomeIcon icon={faFileImport} /> Import price file
                    </button>
                </div>
            </div>

            <div className="pp-filter-bar">
                <div className="pp-filter pp-filter--date">
                    <label className="pp-filter-label" htmlFor="pp-pricing-date">Pricing date</label>
                    <div className="pp-date-nav">
                        <button
                            type="button"
                            className="pp-icon-btn"
                            aria-label="Previous pricing day"
                            title={board?.previous_date ? `Go to ${formatDay(board.previous_date)}` : 'No earlier day has prices'}
                            disabled={loading || !board?.previous_date}
                            onClick={() => goToDay(board.previous_date)}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <input
                            id="pp-pricing-date"
                            type="date"
                            className="pp-input pp-input--date"
                            value={board?.date || query.date}
                            onChange={(event) => goToDay(event.target.value)}
                        />
                        <button
                            type="button"
                            className="pp-icon-btn"
                            aria-label="Next pricing day"
                            title={board?.next_date ? `Go to ${formatDay(board.next_date)}` : 'No later day has prices'}
                            disabled={loading || !board?.next_date}
                            onClick={() => goToDay(board.next_date)}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>
                <FilterSelect
                    id="pp-filter-bate"
                    label="Price point"
                    value={query.bate}
                    onChange={(value) => setFilter('bate', value)}
                    options={PRICE_POINTS}
                />
                <FilterSelect
                    id="pp-filter-region"
                    label="Region"
                    value={query.region}
                    onChange={(value) => setFilter('region', value)}
                    options={regionOptions}
                    allLabel="All regions"
                />
                <FilterSelect
                    id="pp-filter-product"
                    label="Product"
                    value={query.product}
                    onChange={(value) => setFilter('product', value)}
                    options={productOptions}
                    allLabel="All products"
                />
                <FilterSelect
                    id="pp-filter-unit"
                    label="Unit"
                    value={query.unit}
                    onChange={(value) => setFilter('unit', value)}
                    options={unitOptions}
                    allLabel="All units"
                />
            </div>

            {summary ? (
                <div className="pp-summary">
                    <span className="pp-summary-counts">
                        {plural(summary.indexes, 'index', 'indexes')}
                        {' · '}{formatCount(summary.confirmed)} confirmed
                        {' · '}{formatCount(summary.manual)} manual
                        {summary.missing ? ` · ${formatCount(summary.missing)} with no price` : ''}
                    </span>
                    <span className="pp-summary-upload" title={lastImport?.file_name || undefined}>
                        {lastImport
                            ? `Last upload: ${[
                                formatDateTime(lastImport.uploaded_at),
                                lastImport.uploaded_by_name,
                                lastImport.source_label,
                            ].filter(Boolean).join(' · ')}`
                            : 'No price file uploaded yet.'}
                    </span>
                </div>
            ) : null}

            {error ? (
                <LoadProblem message={error} onRetry={reload} />
            ) : (
                <div className={`pp-table-scroll ${loading && board ? 'pp-dim' : ''}`}>
                    <table className="pp-table pp-table--prices">
                        <caption className="pp-sr-only">
                            {board ? `${priceLabel} prices for ${formatDay(board.date)}` : 'Benchmark prices'}
                        </caption>
                        <thead>
                            <tr>
                                <th scope="col">Benchmark</th>
                                <th scope="col">Code</th>
                                <th scope="col">Region</th>
                                <th scope="col">Location</th>
                                <th scope="col">Basis</th>
                                <th scope="col">Unit</th>
                                <th scope="col" className="pp-num">{priceLabel}</th>
                                <th scope="col">Status</th>
                                <th scope="col"><span className="pp-sr-only">Price history</span></th>
                                <th scope="col"><span className="pp-sr-only">More actions</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !board ? (
                                <tr><td colSpan={COLUMNS} className="pp-state">Loading prices…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={COLUMNS} className="pp-state">{emptyText}</td></tr>
                            ) : rows.map((row) => {
                                const { benchmark, assessment } = row;
                                const isManual = assessment?.status === 'manual';
                                return (
                                    <tr key={benchmark.id}>
                                        <td className="pp-cell-strong">{benchmark.name}</td>
                                        <td><span className="pp-code">{benchmark.code}</span></td>
                                        <td>{textOrDash(benchmark.region_label)}</td>
                                        <td>{textOrDash(benchmark.location)}</td>
                                        <td>{textOrDash(benchmark.basis)}</td>
                                        <td className="pp-nowrap">{unitLabel(benchmark.currency, benchmark.unit)}</td>
                                        <td className="pp-num">
                                            {assessment ? formatPrice(assessment.value) : <Dash />}
                                        </td>
                                        <td><PriceStatus assessment={assessment} /></td>
                                        <td className="pp-cell-icon">
                                            <button
                                                type="button"
                                                className="pp-icon-btn"
                                                aria-label={`Price history for ${benchmark.code}`}
                                                title="Price history"
                                                onClick={() => setHistoryFor(benchmark)}
                                            >
                                                <FontAwesomeIcon icon={faChartLine} />
                                            </button>
                                        </td>
                                        <td className="pp-cell-icon">
                                            {isManual ? (
                                                <RowMenu
                                                    label={`More actions for ${benchmark.code}`}
                                                    items={[
                                                        {
                                                            label: 'Edit price',
                                                            icon: faPen,
                                                            onSelect: () => setPriceForm({
                                                                mode: 'edit', row, date: board.date, bate: board.bate,
                                                            }),
                                                        },
                                                        {
                                                            label: 'Delete price',
                                                            icon: faTrash,
                                                            danger: true,
                                                            onSelect: () => setDeleting({ row, date: board.date, bate: board.bate }),
                                                        },
                                                    ]}
                                                />
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {importing ? (
                <ImportPriceFileModal onClose={() => setImporting(false)} onImported={handleImported} />
            ) : null}

            {priceForm ? (
                <EnterPriceModal
                    mode={priceForm.mode}
                    row={priceForm.row}
                    date={priceForm.mode === 'edit' ? priceForm.date : board?.date}
                    bate={priceForm.mode === 'edit' ? priceForm.bate : query.bate}
                    onClose={() => setPriceForm(null)}
                    onSaved={handlePriceSaved}
                />
            ) : null}

            {historyFor ? (
                <PriceHistoryModal
                    benchmark={historyFor}
                    bate={board?.bate || query.bate}
                    onClose={() => setHistoryFor(null)}
                />
            ) : null}

            {deleting ? (
                <DeleteConfirmationModal
                    isOpen
                    onClose={() => setDeleting(null)}
                    onConfirm={confirmDelete}
                    loading={deleteBusy}
                    title="Delete this manual price?"
                    message={`The ${priceLabelFor(deleting.bate)} price typed by hand for ${deleting.row.benchmark.code} `
                        + `on ${formatDay(deleting.date)} is removed. That day shows "No price" until a price file `
                        + 'or a new entry fills it.'}
                    itemName={`${deleting.row.benchmark.code} · ${formatPrice(deleting.row.assessment.value)} `
                        + unitLabel(deleting.row.benchmark.currency, deleting.row.benchmark.unit)}
                    deleteButtonText="Delete price"
                />
            ) : null}
        </div>
    );
};

export default MarketPricesTab;
